import { forwardRef, useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import type { LocationPhoneSelection, PersonPhoneSelection } from '@frontend/thread-header-context';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, Text, Truncated, usePopoverMenu } from '@frontend/design-system';
import { LocationPhoneSelectionPopoverMenuContents } from './location-phone-selection-popover-menu-contents';
import { useBatchGetThreadStatusQuery } from './use-batch-get-thread-status-query';

export type LocationPhoneSelectorProps = {
  selectedPersonPhone: PersonPhoneSelection;
  selectedLocationPhone: LocationPhoneSelection;
  onSelectLocationPhone: (selection: LocationPhoneSelection, threadId?: string) => void;
  groupParticipants?: string[];
  isGroup?: boolean;
  variant?: 'normal' | 'small';
  color?: 'white' | 'default';
  hideCurrentLocationPhone?: boolean;
  disableAutoThreadOpen?: boolean;
  popoverState?: ReturnType<typeof usePopoverMenu>;
  /** When true, skips "Recent Activity" view and shows all numbers directly */
  isNewThreadCreation?: boolean;
} & CommonHTMLAttributes;

export const LocationPhoneSelector = forwardRef<HTMLSpanElement, LocationPhoneSelectorProps>(
  (
    {
      selectedPersonPhone,
      selectedLocationPhone,
      onSelectLocationPhone,
      groupParticipants,
      isGroup,
      variant = 'normal',
      color = 'default',
      hideCurrentLocationPhone = false,
      disableAutoThreadOpen = false,
      popoverState: providedPopoverState,
      isNewThreadCreation,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation('thread-header');
    const { selectedLocationIds } = useAppScopeStore();
    const locationIdsWithACL = filterLocationIdsByACLs({
      locationIds: selectedLocationIds,
      acls: Permission.SMS_INBOX_MANAGE,
    });
    const isMultiLocation = locationIdsWithACL.length > 1;
    const { availableConversationsNumbersByLocation, locationPhonesQuery } = useBatchGetThreadStatusQuery({
      personPhone: selectedPersonPhone?.personPhone,
      groupParticipants,
      isGroup,
    });
    const isLoading = locationPhonesQuery.isLoading;
    const selectedLocationIdAvailableNumbers = selectedLocationPhone.locationId
      ? (availableConversationsNumbersByLocation[selectedLocationPhone.locationId] || []).map(
          (number) => number.locationPhone
        )
      : [];
    const hasMultipleAvailableNumbersForSelectedLocation = selectedLocationIdAvailableNumbers.length > 1;
    const allLocationPhones = useMemo(() => locationPhonesQuery.data.allLocationPhones || [], [locationPhonesQuery]);
    const buttonLabel = useMemo(() => {
      // Show placeholder when no phone is selected
      if (!selectedLocationPhone.locationPhone) {
        return t('Select a number');
      }

      const matchingPhone = allLocationPhones.find(
        (phone) => phone.locationPhone === selectedLocationPhone.locationPhone
      );
      if (!matchingPhone) return formatPhoneNumber(selectedLocationPhone.locationPhone, true);

      return matchingPhone.displayName || formatPhoneNumber(matchingPhone.locationPhone, true);
    }, [allLocationPhones, selectedLocationPhone.locationPhone, t]);

    const internalPopoverState = usePopoverMenu({ placement: 'bottom-start' });
    const popoverState = providedPopoverState || internalPopoverState;
    const hasLocationPhoneOptions = allLocationPhones.length > 1;

    if (isLoading || !hasLocationPhoneOptions) {
      return null;
    }

    // show popover if there are multiple available numbers for the selected location
    // For new thread creation, always show popover when there are multiple phone options
    const showPopover = isNewThreadCreation
      ? hasLocationPhoneOptions
      : hasMultipleAvailableNumbersForSelectedLocation || isMultiLocation;

    return (
      <>
        <span
          css={{
            display: 'flex',
            alignItems: 'center',
            columnGap: 2,
            justifyContent: 'flex-start',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          {...rest}
          data-isopen={popoverState.isOpen}
          data-testid='inbox-thread-header-location-phone-selector'
          ref={ref}
        >
          {variant === 'normal' && (
            <Text color='subdued' size='small' className='from-text'>
              {t('From:')}
            </Text>
          )}
          <Button
            variant='tertiary'
            css={{
              ...{
                padding: theme.spacing(0.375, 0.25),
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                height: 24,
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(0.5),
              },
              ...(showPopover
                ? {
                    '&:hover, &:active:not([aria-disabled="true"])': {
                      background: theme.colors.neutral5,
                    },
                  }
                : {
                    cursor: 'default',
                    '&:hover, &:focus, &:active:not([aria-disabled="true"])': {
                      background: 'transparent',
                    },
                  }),
            }}
            {...popoverState.getTriggerProps()}
            onClick={(e) => {
              if (showPopover) {
                popoverState.getTriggerProps().onClick?.(e);
              }
            }}
            trackingId={`${InboxPrefixes.Thread}-from-location-phone-selector-button`}
          >
            <Icon name='porting-numbers-small' height={12} width={12} color='subdued' />
            <Truncated
              as={Text}
              size='small'
              color='light'
              css={{
                maxWidth: '200px',
                fontWeight: 'bold',
              }}
              className='from-label'
            >
              {buttonLabel}
            </Truncated>
            {showPopover && (
              <Icon
                name='caret-down-tiny'
                className='caret-icon'
                color={color === 'white' ? 'white' : 'light'}
                style={{
                  rotate: popoverState.isOpen ? '-180deg' : '0deg',
                  transition: 'rotate 200ms ease-in-out',
                  minWidth: 'min-content',
                }}
                height={8}
                width={8}
                fontWeight={800}
              />
            )}
          </Button>
          <PopoverMenu
            {...popoverState.getMenuProps()}
            css={{ maxWidth: 300, maxHeight: '400px !important', padding: 0 }}
            alwaysRender
          >
            <LocationPhoneSelectionPopoverMenuContents
              activeIndex={popoverState.activeIndex ?? -1}
              disableAutoThreadOpen={disableAutoThreadOpen}
              getItemProps={popoverState.getItemProps}
              hideCurrentLocationPhone={hideCurrentLocationPhone}
              isOpen={popoverState.isOpen}
              onSelectLocationPhone={(locationPhone, locationId, threadId) => {
                onSelectLocationPhone({ locationPhone, locationId }, threadId);
                popoverState.close();
              }}
              selectedLocationPhone={selectedLocationPhone}
              selectedPersonPhone={selectedPersonPhone}
              groupParticipants={groupParticipants}
              isGroup={isGroup}
              isNewThreadCreation={isNewThreadCreation}
            />
          </PopoverMenu>
        </span>
      </>
    );
  }
);
LocationPhoneSelector.displayName = 'LocationPhoneSelector';
